/*
{
"delivery_state": "REQUESTED",
"payment_state": "UNINVOICED",
"organisation": 1
}

*/

import {
  Typography,
  Alert,
  FormControl,
  TextField,
  Button,
  LinearProgress,
  Switch,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Paper,
  IconButton,
  Chip,
} from '@mui/material';
import React, { useContext, useState } from 'react';

import { emptyDriver } from './driver-form.utils';
import {
  CreateDriverFormProps,
  DriverType,
} from '../../../../interfaces/delivery-company/drivers';
import MuiPhoneNumber from 'mui-phone-number';
import Grid2 from '@mui/material/Unstable_Grid2';
import ConfirmAction from '../../../common/confirm-action/confirm-action';
import axios, { AxiosResponse } from 'axios';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { UserType } from '../../../../enums/users';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import MoneyInput from '../../../common/money-input/money-input';
import { MuiTelInput } from 'mui-tel-input';
import { isMobile, isTablet } from 'react-device-detect';
import { DataResources, ResourceActionType, user_has_data_model_access, user_is_admin } from '../../../../utils/permissions';
import PasswordField from '../../../common/password-field/password-field';
import { set } from 'lodash';
import { Expense } from '../../../../interfaces/delivery-company/expenses';
import { useSnackBar } from '../../../../contexts/snackbar';
import PasswordDisplayDialog from '../../../common/password-display-dialog/password-display-dialog';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { Key, Password } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

function CreateOrEditDriver({
  defaultDriver,
  headless,
  onConnectQRCode,
  onConnectPINCode,
  onChangePassword,
  canChangeDriver: canChangeDriverProp,
}: CreateDriverFormProps) {
  const [driverData, setDriverData] = useState<DriverType | undefined>(
    defaultDriver || emptyDriver
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [confirmDeleteDriver, setConfirmDeleteDriver] =
    useState<boolean>(false);
  const [confirmDeactivation, setConfirmDeactivation] =
    useState<boolean>(false);
  const [response, setResponse] = useState<AxiosResponse>();
  const [message, setMessage] = useState<string>('');
  const { user, config } = useContext(AuthContext);
  const {showMessage} = useSnackBar();

  const canCreateDriver = user_has_data_model_access(user, DataResources.DRIVER, ResourceActionType.CREATE)
  const canDeleteDriver = user_has_data_model_access(user, DataResources.DRIVER, ResourceActionType.DELETE)
  const canChangeDriver = user_has_data_model_access(user, DataResources.DRIVER, ResourceActionType.UPDATE)

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (driverData) {
      setIsLoading(true);
      setResponse(undefined);
      setLoadingMessage('Saving Driver...');
      const requestData = {
        name: driverData.name,
        phone_number: driverData.phone_number,
        default_commission: driverData.default_commission,
        default_pickup_commission: driverData.default_pickup_commission,
        organisation: user?.organisation,
        password: driverData.password,
        commission_settlement_mode: driverData.commission_settlement_mode,
        location_tracking_enabled: driverData.location_tracking_enabled,
      };

      try {
        const endpoint = `${API_ENDPOINT}/api/v1/driver/${driverData.id}/`;

        let response;

        if (driverData.id === '') {
          console.log('request data:', requestData);
          // create new
          response = await axios.post(
            `${API_ENDPOINT}/api/v1/drivers/`,
            requestData,
            config
          );
        } else {
          // edit existing
          response = await axios.put(endpoint, requestData, config);
        }
        setResponse(response);
        setMessage('Driver saved successfully');
      } catch (e: any) {
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          setMessage('Failed to save driver: ' + e.response?.statusText);
        } else {
          setMessage('Failed to save driver');
        }
      }
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const handleReactivate = async (e: any) => {
    e.preventDefault();
    if (driverData) {
      setIsLoading(true);
      setResponse(undefined);
      setLoadingMessage('Reactivating Driver...');
      const requestData = {
        name: driverData.name,
        phone_number: driverData.phone_number,
        default_commission: driverData.default_commission,
        organisation: user?.organisation,
        is_deleted: false,
      };

      try {
        const endpoint = `${API_ENDPOINT}/api/v1/driver/${driverData.id}/`;

        let response;

        if (driverData.id === '') {
          console.log('request data:', requestData);
          // create new
          response = await axios.post(
            `${API_ENDPOINT}/api/v1/drivers/`,
            requestData,
            config
          );
        } else {
          // edit existing
          response = await axios.put(endpoint, requestData, config);
        }
        setResponse(response);
        setMessage('Driver activated successfully');
        setDriverData({ ...driverData, is_deleted: false });
      } catch (e: any) {
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          setMessage('Failed to reactivate driver: ' + e.response?.statusText);
        } else {
          setMessage('Failed to reactivate driver');
        }
      }
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const deleteDriver = async () => {
    if (driverData && driverData.id !== '') {
      setIsLoading(true);
      setResponse(undefined);
      try {
        const response = await axios.delete(
          `${API_ENDPOINT}/api/v1/driver/${driverData.id}/`,
          config
        );
        setResponse(response);
        setDriverData(undefined);
        setMessage('Driver deleted successfully');
      } catch (e) {
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          setMessage('Failed to delete driver: ' + e.response?.statusText);
          showMessage('Failed to delete driver: ' + e.response?.statusText, 'error');
        } else {
          setMessage('Failed to delete driver');
          showMessage('Failed to delete driver', 'error');
        }
        console.log(e);
      }

      setIsLoading(false);
    }
  };

  const deactivateDriver = async () => {
    if (driverData && driverData.id !== '') {
      setIsLoading(true);
      setResponse(undefined);
      try {
        const response = await axios.delete(
          `${API_ENDPOINT}/api/v1/driver/${driverData.id}/`,
          config
        );
        setResponse(response);
        setDriverData({ ...driverData, is_deleted: true });
        setMessage('Driver deactivate successfully');
      } catch (e) {
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          setMessage('Failed to deactivate driver: ' + e.response?.statusText);
        } else {
          setMessage('Failed to deactivate driver');
        }
        console.log(e);
      }

      setIsLoading(false);
    }
  };

  return (
    <>
      {!headless && (
        <Typography
          variant="h3"
          component="h3"
          sx={{ textAlign: 'center', mt: 3, mb: 3 }}
        >
          {defaultDriver === undefined || defaultDriver.id === ''
            ? 'Create Driver'
            : 'Edit Driver'}
        </Typography>
      )}

      {isLoading ? (
        <>
          {loadingMessage}
          <br />
          <LinearProgress />
        </>
      ) : (
        <>
          {message != '' && (
            <>
              <Alert
                severity={
                  response && response.status >= 200 && response.status < 300
                    ? 'success'
                    : 'error'
                }
              >
                <>
                  {message}

                  {response && response.status >= 300 && (
                    <ErrorsFlattenner rawErrors={response.data} />
                  )}
                </>
              </Alert>
              <br />{' '}
            </>
          )}

          {driverData && (
            <>
              <Paper elevation={0} sx={{ p: 3, mt: 1, maxWidth: 900, mx: 'auto' }}>
                <form onSubmit={handleSubmit}>
                  <Grid2 container spacing={3}>
                    {/* Basic Information */}
                    <Grid2 xs={12} sx={{ mt: driverData.id !== '' ? 2 : 0 }}>
                      <Typography variant="h6">
                        Basic Information
                      </Typography>
                    </Grid2>

                    <Grid2 xs={12} md={6}>
                      <FormControl fullWidth>
                        <TextField
                          autoComplete="off"
                          label="Driver Name"
                          type="text"
                          variant="outlined"
                          required
                          value={driverData.name}
                          disabled={driverData.is_deleted}
                          onChange={(e) =>
                            setDriverData({ ...driverData, name: e.target.value })
                          }
                        />
                      </FormControl>
                    </Grid2>

                    <Grid2 xs={12} md={6}>
                      <FormControl fullWidth>
                        <MuiTelInput
                          autoComplete="off"
                          forceCallingCode
                          label="Phone Number"
                          defaultCountry={'LB'}
                          variant="outlined"
                          required
                          value={driverData.phone_number}
                          disabled={driverData.is_deleted}
                          onChange={(newPhone) => {
                            if (typeof newPhone === 'string') {
                              setDriverData({
                                ...driverData,
                                phone_number: newPhone,
                              });
                            }
                          }}
                        />
                      </FormControl>
                    </Grid2>

                     {/* WhatsApp & Password Management - Only shown when editing existing driver */}
                     {driverData.id !== '' && (onConnectQRCode || onConnectPINCode || onChangePassword) && (
                      <>
                        <Grid2 xs={12}>
                          <Typography variant="h6">
                            WhatsApp & Account Management
                          </Typography>
                        </Grid2>

                        {/* WhatsApp Connection Status */}
                        <Grid2 xs={12}>
                          {driverData.connected_to_whatsapp ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              color="success"
                              label="Connected To WhatsApp"
                              sx={{ mb: 1 }}
                            />
                          ) : (
                            <Alert severity="info" sx={{ mb: 1 }}>
                              This driver is not connected to WhatsApp. Use the buttons below to connect.
                            </Alert>
                          )}
                        </Grid2>

                        <Grid2 xs={12}>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {/* WhatsApp Connection Buttons */}
                            {!driverData.connected_to_whatsapp && (
                              <>
                                <Button
                                  variant="outlined"
                                  startIcon={<QrCode2Icon />}
                                  onClick={onConnectQRCode}
                                  disabled={driverData.is_deleted}
                                  size="small"
                                >
                                  Connect via QR Code
                                </Button>

                                <Button
                                  variant="outlined"
                                  startIcon={<Key />}
                                  onClick={onConnectPINCode}
                                  disabled={driverData.is_deleted}
                                  size="small"
                                >
                                  Connect via PIN Code
                                </Button>
                              </>
                            )}

                            {/* Password Management */}
                            <Button
                              variant="outlined"
                              startIcon={<Password />}
                              onClick={onChangePassword}
                              disabled={!canChangeDriverProp || driverData.is_deleted}
                              size="small"
                            >
                              Generate New Password
                            </Button>
                          </Box>
                        </Grid2>
                      </>
                    )}


                    {/* Commission Settings */}
                    {user_is_admin(user) && (
                      <>
                        <Grid2 xs={12}>
                          <Typography variant="h6" sx={{ mt: 2 }}>
                            Commission Settings
                          </Typography>
                        </Grid2>

                        <Grid2 xs={12} md={6}>
                          <MoneyInput
                            money={driverData.default_commission ?? []}
                            setMoney={(money) =>
                              setDriverData({ ...driverData, default_commission: money })
                            }
                            label={'Default Commission Per Order'}
                          />
                        </Grid2>

                        <Grid2 xs={12} md={6}>
                          <MoneyInput
                            money={driverData.default_pickup_commission ?? []}
                            setMoney={(money) =>
                              setDriverData({
                                ...driverData,
                                default_pickup_commission: money,
                              })
                            }
                            label={'Default Commission Per Pickup'}
                          />
                        </Grid2>

                        {/* Settlement Mode */}
                        <Grid2 xs={12}>
                          <FormControl component="fieldset" fullWidth>
                            <FormLabel component="legend">
                              Commission Settlement Mode
                            </FormLabel>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                              Effective mode: {driverData.effective_settlement_mode || 'NET'} {driverData.commission_settlement_mode === null && '(using organization default)'}
                            </Typography>
                            <RadioGroup
                              value={driverData.commission_settlement_mode || ''}
                              onChange={(e) => {
                                const value = e.target.value === '' ? null : e.target.value as 'NET' | 'GROSS';
                                setDriverData({
                                  ...driverData,
                                  commission_settlement_mode: value,
                                });
                              }}
                            >
                              <FormControlLabel
                                value=""
                                control={<Radio />}
                                label={
                                  <Box>
                                    <Typography variant="body2">
                                      Use Organization Default
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Inherit from organization settings
                                    </Typography>
                                  </Box>
                                }
                              />
                              <FormControlLabel
                                value="NET"
                                control={<Radio />}
                                label={
                                  <Box>
                                    <Typography variant="body2">Net Settlement</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Deduct commissions from collections
                                    </Typography>
                                  </Box>
                                }
                              />
                              <FormControlLabel
                                value="GROSS"
                                control={<Radio />}
                                label={
                                  <Box>
                                    <Typography variant="body2">Gross Settlement</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Separate collection and commission invoices
                                    </Typography>
                                  </Box>
                                }
                              />
                            </RadioGroup>
                          </FormControl>
                        </Grid2>

                        {/* Location Tracking */}
                        <Grid2 xs={12}>
                          <FormControl component="fieldset" fullWidth>
                            <FormLabel component="legend">
                              Location Tracking
                            </FormLabel>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                              {driverData.is_on_duty ? (
                                <Chip 
                                  label="Currently On Duty" 
                                  color="success" 
                                  size="small" 
                                  sx={{ mr: 1 }} 
                                />
                              ) : (
                                <Chip 
                                  label="Currently Off Duty" 
                                  color="default" 
                                  size="small" 
                                  sx={{ mr: 1 }} 
                                />
                              )}
                            </Typography>
                            <RadioGroup
                              value={driverData.location_tracking_enabled === null || driverData.location_tracking_enabled === undefined ? '' : String(driverData.location_tracking_enabled)}
                              onChange={(e) => {
                                const value = e.target.value === '' ? null : e.target.value === 'true';
                                setDriverData({
                                  ...driverData,
                                  location_tracking_enabled: value,
                                });
                              }}
                            >
                              <FormControlLabel
                                value=""
                                control={<Radio />}
                                label={
                                  <Box>
                                    <Typography variant="body2">
                                      Use Organization Default
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Inherit from organization settings
                                    </Typography>
                                  </Box>
                                }
                              />
                              <FormControlLabel
                                value="true"
                                control={<Radio />}
                                label={
                                  <Box>
                                    <Typography variant="body2">Enable Tracking</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Track location when on duty
                                    </Typography>
                                  </Box>
                                }
                              />
                              <FormControlLabel
                                value="false"
                                control={<Radio />}
                                label={
                                  <Box>
                                    <Typography variant="body2">Disable Tracking</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Do not track location
                                    </Typography>
                                  </Box>
                                }
                              />
                            </RadioGroup>
                          </FormControl>
                        </Grid2>
                      </>
                    )}

                    {/* Action Buttons */}
                    <Grid2 xs={12}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, justifyContent: 'center' }}>
                        {!driverData.is_deleted && (
                          <Button
                            size={(isMobile && !isTablet) ? 'small' : 'medium'}
                            type="submit"
                            variant="contained"
                            disabled={
                              (!canCreateDriver && defaultDriver === undefined) ||
                              (!canChangeDriver && defaultDriver !== undefined)
                            }
                          >
                            Save Driver
                          </Button>
                        )}

                        {driverData.id !== '' && (
                          <>
                            <Button
                              size={(isMobile && !isTablet) ? 'small' : 'medium'}
                              onClick={() => {
                                driverData.is_deleted
                                  ? setConfirmDeleteDriver(true)
                                  : setConfirmDeactivation(true);
                              }}
                              variant="outlined"
                              color="error"
                              disabled={!canDeleteDriver}
                            >
                              {driverData.is_deleted
                                ? 'Permanently delete'
                                : 'Deactivate'}{' '}
                              driver
                            </Button>

                            {driverData.is_deleted && (
                              <Button
                                size={(isMobile && !isTablet) ? 'small' : 'medium'}
                                onClick={(e) => handleReactivate(e)}
                                variant="contained"
                                color="success"
                                disabled={!canChangeDriver}
                              >
                                Activate
                              </Button>
                            )}
                          </>
                        )}
                      </Box>
                    </Grid2>
                  </Grid2>
                </form>
              </Paper>
            </>
          )}
        </>
      )}

      <ConfirmAction
        message={'Are you sure you want to delete this driver?'}
        action={deleteDriver}
        isOpen={confirmDeleteDriver}
        handleClose={() => setConfirmDeleteDriver(false)}
        isSevereAction={true}
        confirmSevereActionTarget="delete driver"
      >
        Attention: This action will delete all orders registered for this driver
      </ConfirmAction>

      <ConfirmAction
        message={'Are you sure you want to deactivate this driver?'}
        action={deactivateDriver}
        isOpen={confirmDeactivation}
        handleClose={() => setConfirmDeactivation(false)}
        isSevereAction={false}
        confirmSevereActionTarget="deactivate driver"
      >
        <Alert severity="info">
          This action will not delete associated orders. You can always
          reactivate the driver account in the future.
        </Alert>
      </ConfirmAction>

     
    </>
  );
}

export default CreateOrEditDriver;
